import { useCallback, useEffect, useReducer, useState } from 'react';
import { useToast } from '../../../hooks/toast';
import {
  initialState,
  odontogramReducer,
} from '../../../components/odontogram/odontogram.reducer';
import {
  conditionConfig,
  ModalData,
  PaintCondition,
  ToothCondition,
} from '../../../components/odontogram/odontogram.types';
import {
  carregarDentesIniciais,
  paintOdontogramFromData,
} from '../../../components/odontogram/odontogram.helpers';
import {
  AButton,
  ASelect,
  ATitle,
  Loading,
  OptionFooter,
} from '../../../components';
import { useNavigate, useParams } from 'react-router-dom';
import {
  IClient,
  IDentalProcedureInsert,
  IProcedure,
  IProfessional,
} from '../../../shared/interfaces';
import { Col, Form, Row, Table } from 'antd';
import { FileAddOutlined, AlertOutlined } from '@ant-design/icons';
import * as S from './styles';
import InsertProceduresDental from './InsertProcedure';
import { currency } from '../../../shared/utils/functions';
import Discount from './Discount';
import {
  getDentalTreatment,
  getSelectClients,
  getSelectProcedures,
  getSelectProfessionals,
  postDentalTreatment,
} from '../../../http';
import RemoveProceduresDental from './RemoveProcedure';
import { schemaDentalTreatmentCreate } from '../schemas/yup.dental-treatment.schema';
import { OdontogramRender } from '../../../components/odontogram/odontogram-render';
import { useI18n } from '../../../shared/i18n';

const conditionTranslationKeyByValue: Partial<Record<ToothCondition, string>> =
  {
    [ToothCondition.AMALGAMA]: 'dentalCreateEdit.odontogram.conditions.amalgam',
    [ToothCondition.CARIES]: 'dentalCreateEdit.odontogram.conditions.caries',
    [ToothCondition.ENDODONTIA]:
      'dentalCreateEdit.odontogram.conditions.endodontics',
    [ToothCondition.AUSENTE]: 'dentalCreateEdit.odontogram.conditions.missing',
    [ToothCondition.RESINA]: 'dentalCreateEdit.odontogram.conditions.resin',
    [ToothCondition.IMPLANTE]: 'dentalCreateEdit.odontogram.conditions.implant',
    [ToothCondition.SELANTE]: 'dentalCreateEdit.odontogram.conditions.sealant',
    [ToothCondition.COROA]: 'dentalCreateEdit.odontogram.conditions.crown',
    [ToothCondition.OUTROS]: 'dentalCreateEdit.odontogram.conditions.other',
  };

const DentalCreateEdit: React.FC = () => {
  const { id } = useParams();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [odontogramState, dispatch] = useReducer(
    odontogramReducer,
    initialState,
  );
  const [type, setType] = useState<number>(0);
  const [code, setCode] = useState<number | null>(null);
  const [clients, setClients] = useState<IClient[]>([]);
  const [professionals, setProfessionals] = useState<IProfessional[]>([]);
  const [professional_id, setProfessional_id] = useState<string | null>(null);
  const [client_id, setClient_id] = useState<string | null>(null);
  const [procedures, setProcedures] = useState<IProcedure[]>([]);
  const [type_discount, setType_Discount] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [value_discount, setValue_discount] = useState(0);
  const [total, setTotal] = useState<number>(0);
  const [totalFinal, setTotalFinal] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [modalCreate, setModalCreate] = useState<boolean>(false);
  const [modalRemove, setModalRemove] = useState<boolean>(false);
  const [procedureInsert, setProcedureInsert] =
    useState<IDentalProcedureInsert | null>(null);
  const [proceduresInsert, setProceduresInsert] = useState<
    IDentalProcedureInsert[]
  >([]);
  const [disabled, setDisabled] = useState(false);
  const [disabledField, setDisabledField] = useState<boolean>(false);
  const [condicaoSelecionada, setCondicaoSelecionada] =
    useState<ToothCondition | null>(null);
  const [tooth, setTooth] = useState<ModalData | null>(null);

  const getConditionLabel = useCallback(
    (condition: ToothCondition | PaintCondition) => {
      const translationKey = conditionTranslationKeyByValue[condition];

      return translationKey
        ? t(translationKey)
        : t('dentalCreateEdit.odontogram.invalidCondition');
    },
    [t],
  );

  const loadTitle = useCallback(() => {
    const title =
      id !== 'new'
        ? type > 0
          ? t('dentalCreateEdit.intro.editTreatmentTitle')
          : t('dentalCreateEdit.intro.editBudgetTitle')
        : type > 0
          ? t('dentalCreateEdit.intro.createTreatmentTitle')
          : t('dentalCreateEdit.intro.createBudgetTitle');

    if (!code) {
      return title;
    }

    return `${title} ${t('dentalCreateEdit.intro.codeSuffix', { code })}`;
  }, [code, id, t, type]);

  const listProfessionals = useCallback(async () => {
    try {
      const data = await getSelectProfessionals();
      setProfessionals(data);
    } catch (err) {
      addToast({
        type: 'error',
        title: t('dentalCreateEdit.toast.errorTitle'),
        description: t('dentalCreateEdit.toast.listProfessionalsError'),
      });
    }
  }, [addToast, t]);

  const loadDentalTreatment = useCallback(async () => {
    await getDentalTreatment(id)
      .then((data) => {
        const { treatment, items } = data;
        setCode(treatment.code);
        setClient_id(treatment.client_id);
        setProfessional_id(treatment.professional_id);
        setType(treatment.type);
        setProceduresInsert(items);
        setDisabledField(true);

        if (items && items.length > 0) {
          paintOdontogramFromData(items, dispatch);
        }
      })
      .catch((error) => {
        setDisabledField(false);
        addToast({
          type: 'error',
          title: t('dentalCreateEdit.toast.errorTitle'),
          description:
            error.response.data.error ?? t('dentalCreateEdit.toast.loadError'),
        });
      });
  }, [addToast, id, t]);

  const loadClients = useCallback(async () => {
    await getSelectClients(1)
      .then((data) => setClients(data))
      .catch((error) => {
        addToast({
          type: 'error',
          title: t('dentalCreateEdit.toast.errorTitle'),
          description:
            error.response.data.error ??
            t('dentalCreateEdit.toast.listClientsError'),
        });
      });
  }, [addToast, t]);

  const loadProcedures = useCallback(async () => {
    const data = await getSelectProcedures('dental');
    setProcedures(data);
  }, []);

  const calculateTotal = useCallback(() => {
    let sum = proceduresInsert.reduce(
      (acc, current) => acc + +current.value * 1,
      0,
    );

    const disct = proceduresInsert.reduce(
      (acc, current) => acc + current.discount * 1,
      0,
    );
    sum -= disct;
    let discountValue = 0;
    if (discount) {
      const value_porcent = sum * (discount / 100);
      discountValue = type_discount === 0 ? value_porcent : discount;
    }

    setValue_discount(discountValue);
    setTotal(sum - discountValue);
    setTotalFinal(sum);
  }, [proceduresInsert, type_discount, discount]);

  const handleCreateInsertRemove = useCallback(
    (value: IDentalProcedureInsert, type?: 'create' | 'remove') => {
      setProcedureInsert(value);
      if (type === 'create') {
        setModalCreate(true);
      } else {
        setModalRemove(true);
      }
    },
    [],
  );

  const handleSubmit = useCallback(() => {
    setDisabled(true);
    schemaDentalTreatmentCreate
      .validate({
        type,
        client_id,
        total: totalFinal,
        professional_id,
        items: proceduresInsert,
        discount: value_discount,
      })
      .then(async (validated: any) => {
        await postDentalTreatment(validated)
          .then((response) => {
            const treatmentId = response?.treatment?.id;

            addToast({
              type: 'success',
              title: t('dentalCreateEdit.toast.successTitle'),
              description: t('dentalCreateEdit.toast.createdDescription', {
                type: type
                  ? t('dentalCreateEdit.types.treatment')
                  : t('dentalCreateEdit.types.budget'),
              }),
            });
            if (type === 1) {
              if (!treatmentId) {
                addToast({
                  type: 'warning',
                  title: t('dentalCreateEdit.toast.savedTitle'),
                  description: t(
                    'dentalCreateEdit.toast.approvalRedirectWarning',
                  ),
                });
                setTimeout(() => {
                  navigate('/dentals');
                }, 800);
                return;
              }

              setTimeout(() => {
                navigate(`/dental/${treatmentId}/approval`, { replace: true });
              }, 800);
            } else {
              setTimeout(function () {
                navigate(-1);
              }, 1000);
            }
          })
          .catch((error) => {
            addToast({
              type: 'error',
              title: t('dentalCreateEdit.toast.errorTitle'),
              description:
                error.response.data.error ??
                t('dentalCreateEdit.toast.createError'),
            });
            setDisabled(false);
          });
      })
      .catch((err) => {
        addToast({
          type: 'error',
          title: t('dentalCreateEdit.toast.blankFieldsTitle'),
          description: err.errors,
        });
      })
      .finally(() => {
        setDisabled(false);
      });
  }, [
    odontogramState,
    client_id,
    type,
    professional_id,
    proceduresInsert,
    value_discount,
    totalFinal,
    navigate,
    addToast,
    t,
  ]);

  const renderModalProcedureCreateEdit = () => {
    if (modalCreate) {
      return (
        <InsertProceduresDental
          open={modalCreate}
          close={() => setModalCreate(false)}
          code={code}
          treatment_id={id}
          professional_id={professional_id}
          procedures={procedures}
          tooth={tooth}
          setProceduresInsert={setProceduresInsert}
          procedureInsert={procedureInsert}
          setProcedureInsert={setProcedureInsert}
        />
      );
    }
  };

  const renderModalProcedureRemove = () => {
    if (modalRemove && procedureInsert)
      return (
        <RemoveProceduresDental
          open={modalRemove}
          close={() => setModalRemove(false)}
          code={code}
          procedureInsert={procedureInsert}
          setProceduresInsert={setProceduresInsert}
        />
      );
  };

  useEffect(() => {
    dispatch({ type: 'SET_ARCADAS', payload: carregarDentesIniciais() });
    loadClients();
    loadProcedures();
    listProfessionals();
    setLoading(false);
  }, []);

  useEffect(() => {
    if (id !== 'new') {
      loadDentalTreatment();
    }
  }, [id]);

  useEffect(() => {
    calculateTotal();
  }, [proceduresInsert, type_discount, discount]);

  if (loading) return <Loading load />;

  return (
    <>
      <S.HeaderWrapper>
        <ATitle icon={<FileAddOutlined />}>{loadTitle()}</ATitle>
      </S.HeaderWrapper>
      <S.SectionWrapper>
        <S.StyledCard>
          <S.FormSectionTitle>
            <span className="icon">👤</span>
            {t('dentalCreateEdit.sections.basicInfo')}
          </S.FormSectionTitle>

          <Form layout="vertical" style={{ width: '100%' }}>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  label={t('dentalCreateEdit.fields.procedureType')}
                  required
                  tooltip={t('dentalCreateEdit.tooltips.procedureType')}
                >
                  <ASelect
                    value={type}
                    onChange={(e) => setType(e)}
                    options={[
                      {
                        label: `💊 ${t('dentalCreateEdit.types.treatment')}`,
                        value: 1,
                      },
                      {
                        label: `📋 ${t('dentalCreateEdit.types.budget')}`,
                        value: 0,
                      },
                    ]}
                    disabled={disabledField}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label={t('dentalCreateEdit.fields.patient')}
                  required
                  tooltip={t('dentalCreateEdit.tooltips.patient')}
                >
                  <ASelect
                    showSearch
                    placeholder={t('dentalCreateEdit.placeholders.patient')}
                    optionFilterProp="children"
                    onChange={(e) => setClient_id(e)}
                    filterOption={(input, option: any) =>
                      (option?.label ?? '')
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    value={client_id}
                    allowClear
                    options={clients.map((client) => ({
                      label: client.corporate_name,
                      value: client.id,
                    }))}
                    disabled={disabledField}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label={t('dentalCreateEdit.fields.professional')}
                  required
                  tooltip={t('dentalCreateEdit.tooltips.professional')}
                >
                  <ASelect
                    showSearch
                    placeholder={t(
                      'dentalCreateEdit.placeholders.professional',
                    )}
                    optionFilterProp="children"
                    onChange={(e) => setProfessional_id(e)}
                    filterOption={(input, option: any) =>
                      (option?.label ?? '')
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    value={professional_id}
                    allowClear
                    options={professionals.map((prof) => ({
                      label: prof.name,
                      value: prof.id,
                    }))}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </S.StyledCard>
      </S.SectionWrapper>

      {/* Seção: Odontograma */}
      <S.SectionWrapper>
        <S.StyledCard>
          <S.FormSectionTitle>
            <span className="icon">🦷</span>
            {t('dentalCreateEdit.sections.odontogram')}
          </S.FormSectionTitle>

          <S.OdontogramWrapper>
            <OdontogramRender
              odontogramState={odontogramState}
              dispatch={dispatch}
              condicaoSelecionada={condicaoSelecionada}
              setTooth={setTooth}
              setModalCreate={setModalCreate}
            />
          </S.OdontogramWrapper>

          <S.ConditionsContainer style={{ marginTop: 10 }}>
            <div className="conditions-grid">
              {Object.entries(conditionConfig).map(([key, { nome }]) => (
                <AButton
                  key={key}
                  onClick={() =>
                    setCondicaoSelecionada(Number(key) as ToothCondition)
                  }
                  style={{ margin: '4px' }}
                >
                  {getConditionLabel(Number(key) as ToothCondition) || nome}
                </AButton>
              ))}
              <AButton
                onClick={() => setCondicaoSelecionada(ToothCondition.LIMPAR)}
                color="default"
                style={{ margin: '4px' }}
              >
                {t('dentalCreateEdit.odontogram.conditions.clear')}
              </AButton>
            </div>
          </S.ConditionsContainer>
        </S.StyledCard>
      </S.SectionWrapper>
      <S.SectionWrapper>
        <S.StyledCard>
          <S.FormSectionTitle>
            <span className="icon">⚕️</span>
            {t('dentalCreateEdit.sections.procedures')}
          </S.FormSectionTitle>

          {proceduresInsert.length === 0 ? (
            <S.EmptyStateBox>
              <div className="icon">🦷</div>
              <p className="text">{t('dentalCreateEdit.emptyState.title')}</p>
              <p className="text">
                {t('dentalCreateEdit.emptyState.description')}
              </p>
            </S.EmptyStateBox>
          ) : (
            <>
              <S.TableContainer>
                <Table
                  style={{ width: '100%' }}
                  scroll={{ x: true, y: 270 }}
                  columns={[
                    {
                      title: '#',
                      width: 75,
                      render: (value, record, index) => (
                        <strong>{index + 1}</strong>
                      ),
                    },
                    {
                      title: t('dentalCreateEdit.table.tooth'),
                      dataIndex: 'tooth_number',
                      width: 100,
                      align: 'center' as const,
                    },
                    {
                      title: t('dentalCreateEdit.table.face'),
                      dataIndex: 'face',
                      width: 100,
                      align: 'center' as const,
                    },
                    {
                      title: t('dentalCreateEdit.table.currentState'),
                      dataIndex: 'current_state',
                      width: 180,
                      render: (value: number) => {
                        const nomeDaCondicao = getConditionLabel(
                          value as PaintCondition,
                        );

                        return (
                          nomeDaCondicao ?? (
                            <span style={{ color: 'red' }}>
                              {t(
                                'dentalCreateEdit.odontogram.invalidCondition',
                              )}
                            </span>
                          )
                        );
                      },
                    },
                    {
                      title: t('dentalCreateEdit.table.procedure'),
                      dataIndex: 'procedure_id',
                      width: 200,
                      render: (value: string) => (
                        <>
                          {procedures.find((x) => x.id === value)?.description}
                        </>
                      ),
                    },
                    {
                      title: t('dentalCreateEdit.table.unitValue'),
                      width: 130,
                      render: (cell: IDentalProcedureInsert) => (
                        <strong>
                          {currency(+cell.value - +cell.discount)}
                        </strong>
                      ),
                    },
                    {
                      title: t('dentalCreateEdit.table.discount'),
                      width: 130,
                      render: (cell: IDentalProcedureInsert) => (
                        <>{currency(cell.discount)}</>
                      ),
                    },
                    {
                      title: t('dentalCreateEdit.table.total'),
                      width: 130,
                      render: (cell: IDentalProcedureInsert) => (
                        <strong>{currency(cell.value - cell.discount)}</strong>
                      ),
                    },
                    {
                      title: t('dentalCreateEdit.table.actions'),
                      width: 180,
                      render: (cell: IDentalProcedureInsert) => (
                        <Row gutter={8}>
                          <Col>
                            <AButton
                              type="primary"
                              size="small"
                              onClick={() =>
                                handleCreateInsertRemove(cell, 'create')
                              }
                            >
                              {t('dentalCreateEdit.actions.edit')}
                            </AButton>
                          </Col>
                          <Col>
                            <AButton
                              type="default"
                              danger
                              size="small"
                              onClick={() =>
                                handleCreateInsertRemove(cell, 'remove')
                              }
                            >
                              {t('dentalCreateEdit.actions.remove')}
                            </AButton>
                          </Col>
                        </Row>
                      ),
                    },
                  ]}
                  dataSource={proceduresInsert}
                  pagination={false}
                  bordered
                  size="small"
                />
              </S.TableContainer>

              {/* Resumo Financeiro */}
              <S.SummaryBox>
                <div className="summary-row">
                  <span className="label">
                    {t('dentalCreateEdit.summary.subtotal')}
                  </span>
                  <span className="value">{currency(totalFinal)}</span>
                </div>
                {value_discount > 0 && (
                  <div className="summary-row">
                    <span className="label">
                      {t('dentalCreateEdit.summary.discount')}
                    </span>
                    <span className="value" style={{ color: '#262626' }}>
                      -{currency(value_discount)}
                    </span>
                  </div>
                )}
                <div className="summary-row total-final">
                  <span className="label">
                    {t('dentalCreateEdit.summary.totalToPay')}
                  </span>
                  <span className="value">{currency(total)}</span>
                </div>
              </S.SummaryBox>

              {/* Componente de Desconto */}
              <div style={{ marginTop: 12 }}>
                <Discount
                  code={code}
                  type_discount={type_discount}
                  setType_Discount={setType_Discount}
                  total={total}
                  setDiscount={setDiscount}
                  discount={discount}
                />
              </div>
            </>
          )}
        </S.StyledCard>
      </S.SectionWrapper>

      {/* Informação Importante */}
      {code && (
        <S.SectionWrapper>
          <S.InfoAlert>
            <AlertOutlined className="icon" />
            <p className="text">
              <strong>{t('dentalCreateEdit.savedNotice.title')}</strong>{' '}
              {t('dentalCreateEdit.savedNotice.description', {
                type: type
                  ? t('dentalCreateEdit.types.treatment').toLowerCase()
                  : t('dentalCreateEdit.types.budget').toLowerCase(),
                code,
              })}
            </p>
          </S.InfoAlert>
        </S.SectionWrapper>
      )}

      {/* Footer com Botões de Ação */}
      <S.SectionWrapper>
        <OptionFooter
          width="100%"
          onClickCancel={() => navigate(-1)}
          onClickOk={handleSubmit}
          disabled={disabled}
          showOk={!code}
          cancelText={t('dentalCreateEdit.actions.back')}
          okText={t('common.save')}
        />
      </S.SectionWrapper>

      {renderModalProcedureCreateEdit()}
      {renderModalProcedureRemove()}
    </>
  );
};
export default DentalCreateEdit;
